import type { LensCardType, LensCardView, LensDatasetColumn } from '~/types/leLens';

import { parseGrain } from '../lensGrain';
import { tableMeasureInputs } from '../namedTableRatios';

export type CardSchemaMismatch = {
  missing: Array<string>;
  returned: Array<string>;
};

/**
 * A successful query can still be unusable when a rewritten query no longer
 * returns the fields the saved card is mapped to. That is a broken card, not
 * an empty result: classify it before the chart renderer has to guess.
 *
 * Aggregate calculated fields are valid measures even though they are not
 * present in the raw query response. Grain expressions are resolved against
 * their underlying column, and matching is case-insensitive because BigQuery
 * aliases and saved card mappings do not always preserve the same casing.
 */
export function cardSchemaMismatch(
  view: LensCardView | undefined,
  columns: ReadonlyArray<string>,
  aggregateFields: ReadonlyArray<{ field: { name: string } }> = [],
  datasetColumns: ReadonlyArray<LensDatasetColumn> = [],
  type?: LensCardType,
): CardSchemaMismatch | undefined {
  if (!view) return undefined;

  const returned = new Set(columns.map((column) => column.toLowerCase()));
  const computed = new Set(aggregateFields.map((entry) => entry.field.name.toLowerCase()));
  const named = [
    view.x,
    view.series,
    ...tableMeasureInputs(view, type, datasetColumns),
    view.ratio?.of,
    view.ratio?.per,
  ].filter((column): column is string => Boolean(column));

  const missing = named.filter((column, index, all) => {
    if (all.findIndex((candidate) => candidate.toLowerCase() === column.toLowerCase()) !== index) return false;
    const resolved = parseGrain(column)?.column ?? column;
    return !returned.has(resolved.toLowerCase()) && !computed.has(resolved.toLowerCase());
  });

  return missing.length ? { missing, returned: [...columns] } : undefined;
}

export function cardSchemaMismatchDetail(mismatch: CardSchemaMismatch): string {
  const returned = mismatch.returned.length ? mismatch.returned.join(', ') : 'No fields were returned';
  return `Fields this card needs: ${mismatch.missing.join(', ')}\nFields returned: ${returned}`;
}
